import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../Button';
import FaIcon from '../FaIcon';
import { logGameEvent } from '../../analytics';
import { getGameConfig } from '../../games';
import { useAds } from '../../contexts/AdsContext';
import { useGameResults } from '../../hooks/useGameResults';
import { addUtmSource } from '../../utils/utm';
import { GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES } from '../../ads/googleInterstitialAttributes';
import styles from '../../styles/game/GameEndSection.module.css';
import buttonStyles from '../../styles/Button.module.css';

const hasSponsorContent = (sponsor) => !!(sponsor?.context && sponsor?.sponsor_button_text && sponsor?.sponsor_button_url);

function SponsorContent({ sponsorship, gameConfig, date, previewMode = false }) {
  const url = addUtmSource(sponsorship.sponsor_button_url);
  return (
    <div className={styles.sponsorBlock}>
      <p className={styles.sponsorContext}>
        {previewMode && sponsorship.is_placeholder_context ? <em>{sponsorship.context}</em> : sponsorship.context}
      </p>
      <a href={url} target="_blank" rel="noopener noreferrer" className={styles.sponsorButton}
        {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}
        onClick={(event) => {
          if (previewMode) {
            if (sponsorship.is_placeholder_button_url) event.preventDefault();
            return;
          }
          logGameEvent('sponsor_click', gameConfig.id, date, { sponsor_url: url, sponsor_title: sponsorship.sponsor_title || null });
        }}>
        <FaIcon className="fas fa-external-link" />{sponsorship.sponsor_button_text}
      </a>
    </div>
  );
}

function CompletedGameEndSection({
  sectionRef, won, gameConfig, date, shouldTrack = true, sponsorship,
  sessionState, puzzleConfig, justCompleted = false, presentationReady = true,
}) {
  const navigate = useNavigate();
  const { isAdBreakActive, navigationInterstitialMode, registerNavigationInterstitialCandidate, requestNavigationInterstitial } = useAds();
  const pendingExitRef = useRef(false);
  const mountedRef = useRef(true);
  const [pendingExit, setPendingExit] = useState(false);
  const hasTrackedViewRef = useRef(false);
  const archivePath = `/${gameConfig.pathName}/archivio`;
  const usesAnchorManagedInterstitial = navigationInterstitialMode === 'anchor';
  const isExitPending = !usesAnchorManagedInterstitial && (pendingExit || isAdBreakActive);
  // A sponsor header also makes the puzzle sponsored, even if its footer copy is incomplete.
  const sponsored = !!sponsorship;
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (!shouldTrack || hasTrackedViewRef.current || !presentationReady) return;
    hasTrackedViewRef.current = true;
    logGameEvent('end_section_viewed', gameConfig.id, date, { won: !!won });
  }, [date, gameConfig.id, presentationReady, shouldTrack, won]);

  const handleNavigation = useCallback((event, exitType, targetPath) => {
    // Preserve browser link behavior (new tab/window, middle-click).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (usesAnchorManagedInterstitial) {
      registerNavigationInterstitialCandidate({ gameSlug: gameConfig.id, date, exitType, targetPath });
      return;
    }
    event.preventDefault();
    if (pendingExitRef.current || isAdBreakActive) return;
    pendingExitRef.current = true;
    setPendingExit(true);
    const exit = async () => {
      try {
        await requestNavigationInterstitial({ gameSlug: gameConfig.id, date, exitType, targetPath });
      } catch (_) {
        // Navigation remains available when the ad provider fails.
      } finally {
        pendingExitRef.current = false;
        if (mountedRef.current) {
          setPendingExit(false);
          navigate(targetPath);
        }
      }
    };
    void exit();
  }, [gameConfig.id, date, usesAnchorManagedInterstitial, registerNavigationInterstitialCandidate, isAdBreakActive, requestNavigationInterstitial, navigate]);
  const handleArchiveNavigation = useCallback((event, source = 'end_section') => {
    if (shouldTrack) logGameEvent('archive_clicked', gameConfig.id, date, { from: source });
    handleNavigation(event, 'archive', archivePath);
  }, [shouldTrack, gameConfig.id, date, handleNavigation, archivePath]);
  const handleCircuitNavigation = useCallback((event, targetGame, targetDate) => {
    if (targetGame === gameConfig.id) return;
    const targetConfig = getGameConfig(targetGame);
    if (targetConfig) handleNavigation(event, 'daily_circuit', `/${targetConfig.pathName}/${targetDate}`);
  }, [gameConfig.id, handleNavigation]);
  const archiveLinkProps = useMemo(() => ({
    'data-google-interstitial': usesAnchorManagedInterstitial ? undefined : 'false',
    'data-google-vignette': usesAnchorManagedInterstitial ? undefined : 'false',
    'aria-disabled': isExitPending || undefined,
    tabIndex: isExitPending ? -1 : undefined,
  }), [usesAnchorManagedInterstitial, isExitPending]);
  const { canOpen, showEndActions, openResults } = useGameResults({ gameConfig, date, sessionState, puzzleConfig, won, justCompleted, sponsored, presentationReady,
    onArchive: handleArchiveNavigation, archiveLinkProps, onCircuit: handleCircuitNavigation });

  return (
    <div ref={sectionRef} className={styles.endSection}>
      {hasSponsorContent(sponsorship) && <SponsorContent sponsorship={sponsorship} gameConfig={gameConfig} date={date} />}
      {showEndActions && <div className={styles.buttonsRow}>
        <Button secondary onClick={openResults} disabled={!canOpen}>Vedi i risultati</Button>
        <Link to={archivePath} className={`${buttonStyles.button} ${buttonStyles.secondary}`} onClick={handleArchiveNavigation} {...archiveLinkProps}>
          Esplora l’archivio
        </Link>
      </div>}
    </div>
  );
}

const GameEndSection = forwardRef(({ previewMode = false, ...props }, ref) => {
  // Sponsor previews show the complete footer hierarchy but stay inert.
  if (previewMode) return (
    <div ref={ref} className={styles.endSection}>
      {hasSponsorContent(props.sponsorship) && <SponsorContent {...props} previewMode />}
      <div className={styles.buttonsRow}>
        <Button secondary disabled>Vedi i risultati</Button>
        <Link to={`/${props.gameConfig.pathName}/archivio`} className={`${buttonStyles.button} ${buttonStyles.secondary}`}
          onClick={(event) => event.preventDefault()} aria-disabled="true" tabIndex={-1} {...GOOGLE_INTERSTITIAL_INELIGIBLE_ATTRIBUTES}>Esplora l’archivio</Link>
      </div>
    </div>
  );
  return <CompletedGameEndSection key={`${props.gameConfig.id}:${props.date}:${props.sessionState?.session_id || ''}`} {...props} sectionRef={ref} />;
});

export default GameEndSection;
